'use client';

import { c as _c } from "react/compiler-runtime";
import { useApi } from '@repo/api';
import { useAuth } from '@repo/auth';
import { useQueryClient } from '@repo/tanstack/query';
import { toast, toastActionClassName, ToastCopyPhrase, toastPhraseClassName } from '@repo/ui/components';
import { cn } from '@repo/ui/lib/utils';
import Link from 'next/link';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useCopyLoadDetailsSettings } from '../_components/copy-load-details/copy-load-details-settings-provider.tsx';
import { formatLoadDetails } from '../_components/copy-load-details/formatter.ts';
type CopyFailure = 'settings-error' | Exclude<CopyOutcome, 'copied'>;
type CopyOutcome = 'clipboard-error' | 'copied' | 'fetch-error' | 'no-content';
type CopySettingsPhase = 'error' | 'loading' | 'ready';
type CreationToastRecord = {
  loadId: number;
  title: string;
  userId?: number;
};
// An open toast: one load's, which follows the copy settings, or several loads' links.
type OpenToast = {
  kind: 'load';
  record: CreationToastRecord;
} | {
  kind: 'loads';
};
const copyFailureLabels = {
  'clipboard-error': 'Clipboard unavailable — retry',
  'fetch-error': 'Unable to load details — retry',
  'no-content': 'No selected details available',
  'settings-error': 'Unable to load copy settings — retry'
} satisfies Record<CopyFailure, string>;

// The description of the load-created toast. Each copy phrase reads "Copied" in place on success
// and shows a failure in place of its text. While copy settings load, "copy details" waits; when
// they fail, it retries them.
const CreatedLoadActions = t0 => {
  const $ = _c(19);
  const {
    onCopyDetails,
    onCopyLink,
    onRetrySettings,
    openHref,
    phase
  } = t0;
  const [linkFailure, setLinkFailure] = useState();
  const [detailsFailure, setDetailsFailure] = useState();
  const settle = _temp;
  let t1;
  if ($[0] !== onCopyLink) {
    t1 = () => onCopyLink().then(settle(setLinkFailure));
    $[0] = onCopyLink;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const copyLink = t1;
  let t2;
  if ($[2] !== onCopyDetails || $[3] !== onRetrySettings || $[4] !== phase) {
    t2 = () => phase === "error" ? onRetrySettings().then(_temp2) : onCopyDetails().then(settle(setDetailsFailure));
    $[2] = onCopyDetails;
    $[3] = onRetrySettings;
    $[4] = phase;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const copyDetails = t2;
  const shownDetailsFailure = phase === "error" ? "settings-error" : detailsFailure;
  const t3 = linkFailure ? copyFailureLabels[linkFailure] : "Copy the link";
  let t4;
  if ($[6] !== copyLink || $[7] !== t3) {
    t4 = <ToastCopyPhrase onCopy={copyLink} data-sentry-element="ToastCopyPhrase" data-sentry-source-file="use-load-created-toast.tsx">{t3}</ToastCopyPhrase>;
    $[6] = copyLink;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = phase === "loading";
  const t6 = shownDetailsFailure ? copyFailureLabels[shownDetailsFailure] : "copy details";
  let t7;
  if ($[9] !== copyDetails || $[10] !== t5 || $[11] !== t6) {
    t7 = <ToastCopyPhrase busy={t5} onCopy={copyDetails} data-sentry-element="ToastCopyPhrase" data-sentry-source-file="use-load-created-toast.tsx">{t6}</ToastCopyPhrase>;
    $[9] = copyDetails;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== openHref) {
    t8 = <Link className={toastActionClassName} href={openHref} data-sentry-element="Link" data-sentry-source-file="use-load-created-toast.tsx">Open load</Link>;
    $[13] = openHref;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t4 || $[16] !== t7 || $[17] !== t8) {
    t9 = <>{t4},{" "}{t7}{" "}or{" "}{t8}</>;
    $[15] = t4;
    $[16] = t7;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};

// Every new load number, each a link that reads as text, wrapping with no cut-off.
const CreatedLoadLinks = ({
  loadIds
}: {
  loadIds: number[];
}) => loadIds.map((loadId, index) => <Fragment key={loadId}>
      {index ? ', ' : null}
      <Link className={cn(toastPhraseClassName, 'no-underline hover:underline')} href={`/loads/${loadId}/overview`}>
        {loadId}
      </Link>
    </Fragment>);

/**
 * Shows the load-created toasts, open until the user closes them, and closes them when the
 * signed-in user changes. `showLoadCreatedToast` reads "Load created" with "Copy the link, copy
 * details or Open load". `showDuplicatesCreatedToast` links every new Duplicate and offers nothing
 * else: "Load 7 created successfully", "Loads 7, 8 created successfully", or "M of N loads created
 * successfully" when only M of the N were created, followed by ", K unconfirmed" when K were sent
 * without a confirmed answer.
 */
export const useLoadCreatedToast = () => {
  const api = useApi();
  const {
    user
  } = useAuth();
  const queryClient = useQueryClient();
  const {
    canCopyHaulierRate,
    canCopyShipperRate,
    isReady,
    retry,
    settings,
    status
  } = useCopyLoadDetailsSettings();
  const settingsPhase: CopySettingsPhase = isReady ? 'ready' : status === 'error' ? 'error' : 'loading';
  const copySettings = {
    canCopyHaulierRate,
    canCopyShipperRate,
    isReady,
    phase: settingsPhase,
    retry,
    settings,
    status,
    userId: user?.id
  };
  const copySettingsRef = useRef(copySettings);
  // Creation toasts render outside this tree and read the latest settings when used.
  useEffect(() => {
    copySettingsRef.current = copySettings;
  });
  const creationToastRecordsRef = useRef(new Map<number | string, OpenToast>());
  useEffect(() => {
    return () => {
      for (const id of creationToastRecordsRef.current.keys()) toast.dismiss(id);
      creationToastRecordsRef.current.clear();
    };
  }, [user?.id]);
  const copyLink = async (loadId: number): Promise<CopyOutcome> => {
    if (!navigator.clipboard?.writeText) return 'clipboard-error';
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/loads/${loadId}/overview`);
      return 'copied';
    } catch {
      return 'clipboard-error';
    }
  };
  const copyDetails = async (loadId_0: number): Promise<CopyOutcome> => {
    if (!copySettingsRef.current.isReady) return 'fetch-error';
    let value: string;
    try {
      const fullLoad = await queryClient.fetchQuery(api.loads.get.full.queryOptions({
        path: {
          id: loadId_0
        }
      }));
      const {
        canCopyHaulierRate: canCopyHaulierRate_0,
        canCopyShipperRate: canCopyShipperRate_0,
        settings: settings_0
      } = copySettingsRef.current;
      const carrierRate = settings_0.selectedFields.includes('haulierRate') && canCopyHaulierRate_0 ? await queryClient.fetchQuery(api.bills.carrierRate.get.queryOptions({
        path: {
          loadId: loadId_0
        }
      })) : null;
      value = formatLoadDetails(fullLoad, settings_0, {
        canCopyHaulierRate: canCopyHaulierRate_0,
        canCopyShipperRate: canCopyShipperRate_0,
        carrierRate
      });
    } catch {
      return 'fetch-error';
    }
    if (!value) return 'no-content';
    try {
      if (!navigator.clipboard?.writeText) return 'clipboard-error';
      await navigator.clipboard.writeText(value);
      return 'copied';
    } catch {
      return 'clipboard-error';
    }
  };

  // Shows the load-created toast, or updates it in place when `id` is given. Keying the actions by
  // phase resets them on a phase change, so a copy started before it can't leave a stale failure.
  const showCreationToast = ({
    loadId: loadId_1,
    title,
    userId
  }: CreationToastRecord, id_0?: number | string) => toast.success(title, {
    description: <CreatedLoadActions key={copySettingsRef.current.phase} onCopyDetails={() => copySettingsRef.current.userId === userId ? copyDetails(loadId_1) : Promise.resolve('fetch-error')} onCopyLink={() => copyLink(loadId_1)} onRetrySettings={() => copySettingsRef.current.retry()} openHref={`/loads/${loadId_1}/overview`} phase={copySettingsRef.current.phase} />,
    duration: Infinity,
    id: id_0,
    onDismiss: dismissed => creationToastRecordsRef.current.delete(dismissed.id)
  });
  useEffect(() => {
    for (const [id_1, open] of creationToastRecordsRef.current) {
      if (open.kind === 'load') showCreationToast(open.record, id_1);
    }
  }, [settingsPhase]);
  const showLoadCreatedToast = (loadId_2: number) => {
    const record = {
      loadId: loadId_2,
      title: 'Load created',
      userId: copySettingsRef.current.userId
    };
    creationToastRecordsRef.current.set(showCreationToast(record), {
      kind: 'load',
      record
    });
  };
  const showDuplicatesCreatedToast = (loadIds: number[], total: number, unconfirmed = 0) => {
    const links = <CreatedLoadLinks loadIds={loadIds} />;
    // Every Duplicate created names them in the title; otherwise the title counts them.
    const allCreated = loadIds.length === total;
    const unconfirmedNote = unconfirmed ? `, ${unconfirmed} unconfirmed` : '';
    const title_0 = allCreated ? <>
        {loadIds.length === 1 ? 'Load' : 'Loads'} {links} created successfully
      </> : `${loadIds.length} of ${total} loads created successfully${unconfirmedNote}`;
    const id_2 = toast.success(title_0, {
      description: allCreated ? undefined : links,
      duration: Infinity,
      onDismiss: dismissed_0 => creationToastRecordsRef.current.delete(dismissed_0.id)
    });
    creationToastRecordsRef.current.set(id_2, {
      kind: 'loads'
    });
  };
  return {
    showDuplicatesCreatedToast,
    showLoadCreatedToast
  };
};
function _temp(setFailure) {
  return outcome => {
    setFailure(outcome === "copied" ? undefined : outcome);
    return outcome === "copied";
  };
}
function _temp2() {
  return false;
}